import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const WestPointGrey = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.west-point-grey-vancouver:About West Point Grey Paragraph">
          West Point Grey is a distinguished neighborhood in{' '}
          <LinkText href="/rental-listings/city/vancouver">Vancouver</LinkText>,{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia
          </LinkText>
          , renowned for its scenic beauty and affluent character. Nestled
          between English Bay to the north and 16th Avenue to the south, it
          offers residents and visitors stunning views of the North Shore
          Mountains and the downtown skyline. The area is characterized by its
          historic charm, featuring tree-lined streets and a mix of heritage
          homes and modern residences. Its proximity to beaches like Jericho and
          Spanish Banks, along with access to Pacific Spirit Regional Park,
          makes it a desirable location for those seeking a blend of urban
          convenience and natural beauty.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$2,000 - $2,500',
          bed2: '$3,000 - $4,000',
          bed3: '$5,000 - $6,600'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.west-point-grey-vancouver:Apartment Hunting West Point Grey Paragraph 1">
            Finding an apartment for rent in West Point Grey, Vancouver, BC
            requires careful planning and consideration. Start by determining
            your criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant West Point Grey lifestyle by exploring
            your new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.west-point-grey-vancouver:Apartment Hunting West Point Grey Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.west-point-grey-vancouver:Apartments for rent in West Point Grey Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in West Point Grey Vancouver
              </LinkText>{' '}
              are a popular choice for individuals and young professionals
              looking for a convenient and low-maintenance living option. With a
              variety of sizes and styles available, residents can find the
              perfect space to suit their needs. From sleek and modern high-rise
              buildings to charming heritage apartments, there is something for
              everyone in this bustling neighborhood.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.west-point-grey-vancouver:Houses for rent in West Point Grey Paragraph">
              <LinkText href={links.house}>
                Houses for rent in West Point Grey Vancouver
              </LinkText>{' '}
              present a distinctive living experience for families and those who
              appreciate privacy and outdoor space. With a blend of historic
              homes and contemporary designs, residents can relish the charm and
              character of the neighborhood while still maintaining proximity to
              amenities and attractions.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.west-point-grey-vancouver:Townhouses for rent in West Point Grey Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in West Point Grey Vancouver
              </LinkText>{' '}
              offer a harmonious blend of communal living and personal privacy.
              These multi-level homes with small outdoor spaces are perfect for
              individuals seeking extra room to relax. Townhouse living provides
              the perfect balance, enabling residents to enjoy the advantages of
              a tight-knit community while also having their own secluded space.
              Whether you’re in search of rental townhouses in West Point Grey
              Vancouver or considering purchasing one, these properties present
              a distinct opportunity to immerse yourself in the bustling city
              while enjoying a cozy and expansive abode.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.west-point-grey-vancouver:Basements for rent in West Point Grey Paragraph">
              Basements for rent in West Point Grey Vancouver are a
              cost-effective option for singles or students looking for
              affordable housing. These basements for rent often provide a
              separate entrance and basic amenities, making them a practical
              choice for those on a budget. While basements for rent in West
              Point Grey Vancouver may not offer the same level of natural light
              as other options, they do provide a cozy and private living space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.west-point-grey-vancouver:Condos for rent West Point Grey Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in West Point Grey Vancouver
              </LinkText>{' '}
              are a popular choice for those who value convenience and a sense
              of community. With amenities such as fitness centers, rooftop
              gardens, and concierge services, condos for rent in West Point
              Grey Vancouver offer a maintenance-free lifestyle. Residents can
              enjoy the vibrant energy of the neighborhood while having all the
              comforts of home.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.west-point-grey-vancouver:Rooms for rent in West Point Grey Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in West Point Grey Vancouver
              </LinkText>{' '}
              provide an affordable and flexible housing option. Whether you’re
              a student looking for a shared living arrangement or someone who
              enjoys the social aspect of communal living, renting a room allows
              you to live in the heart of the city while saving on costs. With a
              friendly atmosphere and the opportunity to meet new people,
              renting a room can be a great way to experience West Point Grey
              Vancouver on a budget.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.west-point-grey-vancouver:Short term rentals in West Point Grey Paragraph">
              West Point Grey Vancouver, with its diverse employment
              opportunities and academic institutions, is popular for{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText>,
              especially during summer and the start of school semesters. This
              demand is driven by tourists, business travelers, and students,
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                making short-term rentals a flexible option
              </LinkText>{' '}
              for a variety of needs. The convenience and adaptability of these
              rentals make them a key consideration for those seeking
              accommodation in this vibrant urban area.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.west-point-grey-vancouver:Exploring West Point Grey Paragraph">
          West Point Grey embodies a harmonious blend of natural beauty and
          urban sophistication. This prestigious Vancouver neighborhood is
          celebrated for its panoramic views of{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/english-bay-beach.aspx">
            English Bay
          </LinkText>{' '}
          and the{' '}
          <LinkText href="https://www.tripadvisor.ca/Attractions-g181717-Activities-c57-t66-North_Vancouver_British_Columbia.html">
            North Shore Mountains
          </LinkText>
          . Key attractions include{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/jericho-beach.aspx">
            Jericho Beach
          </LinkText>{' '}
          and{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/spanish-bank-beach.aspx">
            Spanish Banks
          </LinkText>
          , both offering sandy shores and recreational facilities. The area
          exudes a relaxed, village-like ambiance, with Point Grey Village
          serving as a hub for boutique shopping and dining along West 10th
          Avenue. The community’s proximity to the University of British
          Columbia adds an academic vibrancy, while Pacific Spirit Regional Park
          provides lush trails for outdoor enthusiasts. The combination of
          scenic landscapes, cultural richness, and upscale living makes West
          Point Grey a coveted destination.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.west-point-grey-vancouver:Things To Do West Point Grey Paragraph">
          West Point Grey offers a diverse array of activities catering to
          various interests. Nature lovers can explore Pacific Spirit Regional
          Park’s extensive network of trails, perfect for hiking and
          bird-watching. Beach enthusiasts will enjoy{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/jericho-beach.aspx">
            Jericho Beach
          </LinkText>{' '}
          and{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/spanish-bank-beach.aspx">
            Spanish Banks
          </LinkText>
          , ideal for picnics, swimming, and sailing. The{' '}
          <LinkText href="https://jsca.bc.ca/">Jericho Sailing Centre</LinkText>{' '}
          provides opportunities for kayaking and windsurfing. Art aficionados
          can visit the nearby{' '}
          <LinkText href="https://visit.ubc.ca/see-and-do/museums-and-art-galleries/museum-of-anthropology/">
            Museum of Anthropology at UBC
          </LinkText>
          , showcasing indigenous art and cultural artifacts. Shoppers can
          stroll through Point Grey Village on West 10th Avenue, featuring an
          assortment of independent boutiques and cafes. Throughout the year,
          the community hosts events such as the{' '}
          <LinkText href="https://vancouversbestplaces.com/events-calendar/june/vancouver-point-grey-fiesta/">
            Point Grey Fiesta
          </LinkText>
          , a family-friendly festival with parades and entertainment. Whether
          you’re seeking outdoor adventure, cultural experiences, or leisurely
          shopping, West Point Grey offers a rich tapestry of activities.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.west-point-grey-vancouver:Restaurants West Point Grey Paragraph">
        <Paragraph>
          West Point Grey Vancouver offers a range of dining options for every
          taste. Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1) <LinkText href="https://www.burgoo.ca/">Burgoo</LinkText> - Burgoo
          is a beloved bistro known for its comforting, hearty dishes that evoke
          a sense of home-cooked warmth. Specializing in comfort food from
          around the world, their menu features classics like French onion soup,
          mac ’n’ cheese, and beef bourguignon. The cozy, rustic ambiance makes
          it an ideal spot for casual gatherings and intimate dinners.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://fourolives.ca/">
            Four Olives Restaurant
          </LinkText>{' '}
          - Mediterranean eatery celebrated for its authentic Greek flavors and
          warm hospitality. Known as one of the best Greek restaurants in the
          area, it serves up classics like lamb souvlaki, spanakopita, and
          moussaka. The inviting, upscale ambiance paired with hearty, flavorful
          dishes makes it a go-to destination for both casual dinners and
          special occasions.
        </Paragraph>
        <Paragraph>
          3){' '}
          <LinkText href="https://www.jositaliandeli.com/">
            Jo’s Italian Deli
          </LinkText>{' '}
          - a cozy, family-run spot beloved for its handcrafted Italian fare and
          welcoming atmosphere. The deli’s menu includes mouthwatering
          sandwiches, rich pastas, and a curated selection of imported goods.
          With its nostalgic charm and hearty flavors, Jo’s is perfect for a
          quick lunch or a relaxed bite with friends.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.west-point-grey-vancouver:Transportation West Point Grey Paragraph">
          Getting around West Point Grey, Vancouver, BC, is convenient with
          various transportation options. The city’s{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses and the{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , provides easy access to surrounding areas. For cyclists, Vancouver
          features an extensive network of bike lanes and rental services. Many
          parts of West Point Grey are also walkable, with key amenities and
          attractions nearby. Drivers will find a mix of street parking and
          parking garages available. No matter your preferred way of getting
          around, West Point Grey offers accessible and efficient transportation
          choices.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.west-point-grey-vancouver:Where To Stay West Point Grey Paragraph">
          Accommodation options in West Point Grey, Vancouver cater to those
          looking for a more local and immersive experience. This area offers a
          mix of boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, providing
          travelers with a unique stay. While large hotels may be limited within
          West Point Grey itself, its proximity to other parts of Vancouver
          ensures that a range of traditional accommodations, from luxury to
          budget-friendly, is just a short distance away. Staying in West Point
          Grey allows visitors to experience its distinct character while
          remaining conveniently close to the city’s main attractions.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default WestPointGrey
